/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Typography, useTheme } from "@mui/material";
import {
  GridColDef,
  GridColumnGroupingModel,
  GridRenderCellParams,
} from "@mui/x-data-grid-premium";
import { HotkeyCompliantDataGrid } from "src/components/common/dataGrid";
import { useUnitsFormatter } from "hooks/formatters";
import { t, TFunction } from "i18next";
import { useMemo } from "react";
import {
  ConsumptionSourceName,
  MaterialGroupingName,
  MaterialGroupName,
} from "src/store/api/generatedApi";
import { useFormatNumber } from "src/utils/formatNumber";
import { BarCell } from "../BarCell";
import { defaultTableSx } from "../tableFormatting";
import { GroupedConsumptionSeries } from "../types";

interface ConsumptionTableProps {
  consumptionSeries: GroupedConsumptionSeries;
  selectedDate: Date | null;
  selectedMaterialGrouping: MaterialGroupingName | null;
  selectedProductGroup: string | null;
  selectedConsumptionSources: ConsumptionSourceName[];
}

interface ConsumptionRecord {
  materialGroup: MaterialGroupName;
  consumptionSource: ConsumptionSourceName;
  consumedMass: number;
  consumedFraction: number;
}

type ConsumptionRow = {
  // a consumption record that has been pivoted to have
  // columns for each consumption source
  id: number;
  materialGroup: MaterialGroupName;
} & {
  [key: `${ConsumptionSourceName}_mass`]: number;
  [key: `${ConsumptionSourceName}_fraction`]: number;
};

export const ConsumptionTable = ({
  consumptionSeries,
  selectedDate,
  selectedMaterialGrouping,
  selectedProductGroup,
  selectedConsumptionSources,
}: ConsumptionTableProps) => {
  const records = useMemo(
    () =>
      buildRecords(
        consumptionSeries,
        selectedDate,
        selectedMaterialGrouping,
        selectedProductGroup,
        selectedConsumptionSources
      ),
    [
      consumptionSeries,
      selectedDate,
      selectedMaterialGrouping,
      selectedProductGroup,
      selectedConsumptionSources,
    ]
  );
  const materialGroups = useMemo(
    () =>
      consumptionSeries.availableMaterialGroupings[
        selectedMaterialGrouping ?? -1
      ] ?? [],
    [consumptionSeries.availableMaterialGroupings, selectedMaterialGrouping]
  );

  const rows = useMemo(
    () =>
      buildRowsFromRecords(records, selectedConsumptionSources, materialGroups),
    [records, selectedConsumptionSources, materialGroups]
  );

  const totalsRow = useMemo(
    () => buildTotalsRow(rows.length, records, selectedConsumptionSources, t),
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- t is passed as argument to buildTotalsRow; kept to rebuild when locale changes
    [records, selectedConsumptionSources, rows.length, t]
  );

  const { columns, columnGroupingModel } = useBuildColumns(
    selectedMaterialGrouping,
    rows,
    totalsRow,
    selectedConsumptionSources,
    t
  );

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <HotkeyCompliantDataGrid
        rows={rows}
        columns={columns}
        columnGroupingModel={columnGroupingModel}
        sx={defaultTableSx}
        pinnedRows={{
          bottom: [totalsRow],
        }}
      />
    </div>
  );
};

const buildRecords = (
  consumptionSeries: GroupedConsumptionSeries,
  selectedDate: Date | null,
  selectedMaterialGrouping: MaterialGroupingName | null,
  selectedProductGroup: string | null,
  selectedConsumptionSources: ConsumptionSourceName[]
): ConsumptionRecord[] => {
  const records: ConsumptionRecord[] = [];

  const filteredSeries = consumptionSeries.items.filter(
    (item) =>
      item[0].materialGrouping === selectedMaterialGrouping &&
      selectedConsumptionSources.includes(item[0].consumptionSource) &&
      (selectedProductGroup === "all"
        ? item[0].productGrouping === "all" // When "all" is selected, only show aggregated data
        : item[0].productGroup === selectedProductGroup) // When specific group is selected, show that group's data
  );

  for (const [key, series] of filteredSeries) {
    for (const [index, dateString] of series.date.entries()) {
      const date = new Date(dateString);

      if (!selectedDate || date.toISOString() === selectedDate.toISOString()) {
        const consumedMass = series.consumed_mass[index];
        const consumedFraction = series.consumed_fraction[index];

        if (consumedFraction && consumedMass) {
          records.push({
            materialGroup: key.materialGroup,
            consumptionSource: key.consumptionSource,
            consumedFraction: consumedFraction,
            consumedMass: consumedMass,
          });
        }
      }
    }
  }

  return records;
};

const buildRowsFromRecords = (
  records: ConsumptionRecord[],
  availableConsumptionSources: ConsumptionSourceName[],
  materialGroups: MaterialGroupName[]
): ConsumptionRow[] =>
  materialGroups.map((rowMaterialGroup, index) => {
    const row: ConsumptionRow = { id: index, materialGroup: rowMaterialGroup };
    for (const columnConsumptionSource of availableConsumptionSources) {
      const record = records.find(
        ({ materialGroup, consumptionSource }) =>
          materialGroup === rowMaterialGroup &&
          consumptionSource === columnConsumptionSource
      );
      row[`${columnConsumptionSource}_mass`] = record?.consumedMass ?? 0;
      row[`${columnConsumptionSource}_fraction`] =
        record?.consumedFraction ?? 0;
    }
    return row;
  });

const buildTotalsRow = (
  id: number,
  records: ConsumptionRecord[],
  availableConsumptionSources: ConsumptionSourceName[],
  t: TFunction
): ConsumptionRow => {
  const totalMassesBySource = new Map<ConsumptionSourceName, number>();

  for (const record of records) {
    totalMassesBySource.set(
      record.consumptionSource,
      (totalMassesBySource.get(record.consumptionSource) ?? 0) +
        record.consumedMass
    );
  }

  const sourceFields: {
    [key: `${ConsumptionSourceName}_mass`]: number;
    [key: `${ConsumptionSourceName}_fraction`]: number;
  } = {};
  for (const source of availableConsumptionSources) {
    const sourceKey = source;
    sourceFields[`${sourceKey}_mass`] = totalMassesBySource.get(source) ?? 0;
    sourceFields[`${sourceKey}_fraction`] = 1.0;
  }

  return {
    id: id,
    materialGroup: t("Total"),
    ...sourceFields,
  };
};

const useBuildColumns = (
  selectedMaterialGrouping: MaterialGroupingName | null,
  rows: ConsumptionRow[],
  totalsRow: ConsumptionRow,
  selectedConsumptionSources: ConsumptionSourceName[],
  t: TFunction
): {
  columns: GridColDef<ConsumptionRow>[];
  columnGroupingModel: GridColumnGroupingModel;
} => {
  const units = useUnitsFormatter(false);
  const theme = useTheme();

  const formatNumber = useFormatNumber();

  const columns: GridColDef<ConsumptionRow>[] = [
    {
      field: "materialGroup",
      headerName: t(selectedMaterialGrouping ?? ""),
      flex: 3,
      valueFormatter: (value?: string) => {
        const translated = !value ? "" : t(value);
        return translated;
      },
    },
  ];
  const columnGroupingModel: GridColumnGroupingModel = [];

  for (const source of selectedConsumptionSources) {
    columns.push({
      field: `${source}_mass`,
      type: "number",
      headerName: units("mass"),
      flex: 2,
      align: "right",
      renderCell: (params: GridRenderCellParams<ConsumptionRow, number>) =>
        params.row.id === rows.length ? (
          <Typography variant="body1" component="div">
            {formatNumber(0, true)(totalsRow[`${source}_mass`] ?? 0)}
          </Typography>
        ) : (
          <BarCell
            value={params.value!}
            formattedValue={formatNumber(0, true)(params.value)}
            maxValue={totalsRow[`${source}_mass`] ?? 0}
            color={theme.palette.data.blue}
          />
        ),
      cellClassName: "coloured-cell",
    });

    columns.push({
      field: `${source}_fraction`,
      type: "number",
      headerName: "%",
      flex: 1.5,
      align: "right",
      renderCell: (params: GridRenderCellParams<ConsumptionRow, number>) => (
        <Typography variant="body1" component="div">
          {!params.value
            ? ""
            : `${formatNumber(1, true)(params.value * 100)} %`}
        </Typography>
      ),
    });

    columnGroupingModel.push({
      groupId: source,
      headerName: t(source),
      children: [
        {
          field: `${source}_mass`,
        },
        { field: `${source}_fraction` },
      ],
    });
  }

  return { columns, columnGroupingModel };
};
